/**
 * TalentAgreement — Hardened E-Sign screen (2026-08-30).
 *
 * Flow:
 *   1. GET /api/icma/current            — active agreement (rendered HTML)
 *   2. GET /api/icma/prepare-signature  — locked signer identity
 *   3. Talent reviews the agreement
 *   4. Talent checks the signature-intent acknowledgment (NOT preselected)
 *   5. Talent types their verified legal name exactly (case-insensitive match)
 *   6. Talent requests OTP → server sends email/SMS; only the hash is stored
 *   7. Talent enters the OTP
 *   8. POST /api/icma/accept — all evidence bundled into an immutable
 *      executed copy (agreement + Signature Certificate). The certificate
 *      is baked into the HTML BEFORE the executed_copy_hash is computed
 *      so the audit chain cannot show the agreement without the
 *      certificate.
 *
 * NB: The signer name is NEVER editable on this screen. It is populated
 * from the account's verified IDV data (or profile name fallback with a
 * clear IDV-pending banner).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Mail, Phone, Lock, AlertTriangle, FileText } from 'lucide-react';
import api from '../../utils/api';
import { executedCopyHref } from '../../utils/executedCopyUrl';
import { reportFrontendError } from '../../utils/errorBeacon';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

async function sha256(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const ACCEPT_SCREEN_TEXT = `By selecting I Accept & Electronically Sign, I confirm that I am the person identified above, I intend to electronically sign this Agreement, and I agree that my electronic signature has the same effect as my handwritten signature.

I have read, understand, and agree to be bound by the iEvntür Independent Contractor and Marketplace Agreement, including its arbitration and class-action waiver provisions to the maximum extent permitted by law.

I understand that iEvntür will preserve an immutable executed copy of this Agreement with a Signature Certificate evidencing my identity, the date and time of signing, my IP address and device, the agreement's cryptographic hash, and a verification of the one-time code sent to my account.`;

function _norm(s) { return (s || '').trim().replace(/\s+/g, ' ').toLowerCase(); }

export default function TalentAgreement() {
  const navigate = useNavigate();
  const [agreement, setAgreement]     = useState(null);
  const [identity, setIdentity]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [reloadKey, setReloadKey]     = useState(0);
  const [confirmedIntent, setIntent]  = useState(false);
  const [typedName, setTypedName]     = useState('');
  const [otpChannel, setOtpChannel]   = useState('email');
  const [challenge, setChallenge]     = useState(null); // { challenge_id, destination_masked, expires_in_seconds }
  const [otp, setOtp]                 = useState('');
  const [sendingOtp, setSendingOtp]   = useState(false);
  const [signing, setSigning]         = useState(false);
  const [expiresAt, setExpiresAt]     = useState(0);
  const [now, setNow]                 = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load the agreement and the signer identity INDEPENDENTLY. A
      // transient failure on prepare-signature (e.g. server waking during
      // a deploy) must NOT blank the whole screen into the misleading
      // "No active agreement" dead-end — the talent could then never open
      // the agreement to accept it (prod incident 2026-09-04).
      try {
        const a = await api.get('/icma/current');
        if (!cancelled) setAgreement(a.data);
      } catch (e) {
        const status = e?.response?.status;
        const code = e?.response?.data?.detail?.code;
        if (!cancelled) {
          if (status === 404 || (status === 503 && code === 'agreement_update_pending')) {
            setAgreement(null);
          } else {
            setAgreement(null);
            setLoadError(true);
            reportFrontendError({
              context: 'talent_agreement_load_failed',
              message: `GET /icma/current failed (status ${status ?? 'network'})`,
              detail: e?.message,
            });
          }
        }
      }
      try {
        const i = await api.get('/icma/prepare-signature');
        if (!cancelled) {
          setIdentity(i.data);
          setIdentityError(false);
          // Prefer SMS when a phone is on file — email delivery can be
          // globally paused for domain-reputation reasons, which would
          // otherwise strand the talent on a code that never arrives.
          if (i.data?.has_phone) setOtpChannel('sms');
        }
      } catch (err) {
        if (!cancelled) setIdentityError(true);
        reportFrontendError({
          context: 'talent_agreement_identity_failed',
          message: `GET /icma/prepare-signature failed (status ${err?.response?.status ?? 'network'})`,
          detail: err?.message,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const nameLocked = !!identity && identity.legal_name_locked;
  const nameMatches = useMemo(() => {
    if (!identity) return false;
    const typed = (typedName || '').trim();
    if (nameLocked) {
      // Verified legal name on file — must match exactly.
      return _norm(typed) === _norm(identity.legal_name);
    }
    // Self-entered legal name — must be a plausible full name (first + last).
    return typed.length >= 3 && typed.split(/\s+/).filter(Boolean).length >= 2;
  }, [identity, typedName, nameLocked]);
  const otpValid = /^\d{6}$/.test(otp);
  const secondsLeft = Math.max(0, Math.floor((expiresAt - now) / 1000));
  const canSign =
    confirmedIntent && nameMatches && challenge && otpValid && !signing;

  const requestOtp = async () => {
    if (!agreement || !identity) return;
    if (otpChannel === 'sms' && !identity.has_phone) {
      toast.error('No phone number on file — use email instead.');
      return;
    }
    setSendingOtp(true);
    try {
      const r = await api.post('/icma/otp/challenge', {
        agreement_version: agreement.agreement_version,
        channel: otpChannel,
      });
      setChallenge(r.data);
      setExpiresAt(Date.now() + (r.data.expires_in_seconds || 600) * 1000);
      toast.success(`Verification code sent to ${r.data.destination_masked}`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message ||
        e?.response?.data?.detail?.error ||
        'Unable to send verification code.',
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const onSign = async () => {
    if (!canSign || !agreement || !identity) return;
    setSigning(true);
    try {
      const rendered_body_hash = await sha256(agreement.body_html);
      if (rendered_body_hash !== agreement.body_hash) {
        toast.error('Agreement integrity check failed — please refresh and retry.');
        setSigning(false);
        return;
      }
      const acceptance_screen_hash = await sha256(ACCEPT_SCREEN_TEXT);
      const r = await api.post('/icma/accept', {
        agreement_version: agreement.agreement_version,
        rendered_body: agreement.body_html,
        rendered_body_hash,
        acceptance_screen_hash,
        typed_legal_name: typedName,
        signature_intent_ack: true,
        otp_challenge_id: challenge.challenge_id,
        otp_code: otp,
      });
      toast.success('Agreement executed. You can now resume applying for gigs.');
      const url = r?.data?.executed_copy_url;
      if (url) {
        // Fire the executed copy in a new tab so talent has instant proof.
        // Must carry ?token= — a browser navigation can't send the
        // Authorization header (was the "unauthenticated" bug).
        try {
          window.open(executedCopyHref(url), '_blank', 'noopener,noreferrer');
        } catch (_) { /* noop */ }
      }
      navigate('/talent/dashboard');
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const expected = e?.response?.data?.detail?.expected_display;
      if (code === 'legal_name_mismatch') {
        toast.error(`Please type your legal name exactly as shown: ${expected}`);
      } else if (code?.startsWith('otp_')) {
        toast.error(`Verification code failed (${code.slice(4)}). Send a new code and try again.`);
        setChallenge(null); setOtp('');
      } else {
        toast.error(e?.response?.data?.detail?.message || 'Signature failed. Try again.');
      }
    } finally {
      setSigning(false);
    }
  };

  const retry = () => {
    setLoading(true);
    setLoadError(false);
    setIdentityError(false);
    setReloadKey((k) => k + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin" data-testid="agreement-loading" />
      </div>
    );
  }

  // Transient failure loading the agreement (e.g. server waking during a
  // deploy). Offer a retry instead of the misleading "no agreement" screen.
  if (loadError && !agreement) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="agreement-load-error">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Couldn’t load the agreement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">
              We couldn’t open the agreement just now. This is usually
              temporary — please try again.
            </p>
            <div className="mt-4 flex gap-2">
              <Button onClick={retry} data-testid="agreement-retry-btn">Try again</Button>
              <Button variant="outline" onClick={() => navigate('/talent/dashboard')} data-testid="agreement-back-btn">
                Back to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="agreement-none">
        <Card>
          <CardHeader>
            <CardTitle>No active iEvntür Contractor Agreement</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">
              No agreement is currently published for review. Continue using
              the Platform under your prior agreement until a new version is
              posted.
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate('/talent/dashboard')}
              data-testid="agreement-back-btn"
            >
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const idvVerified = identity?.identity_source === 'idv_verified';

  // 2026-08-30 P0 (Formatting cleanup): the contract must read as a
  // professionally prepared legal document. We removed the cramped
  // inner scroll box (max-h + overflow), added generous margins, a
  // large document-style title with a thin cyan divider, and applied
  // a `.icma-doc` typography rules block so every h1/h2/h3/ul/ol/li
  // gets proper spacing regardless of the source Markdown. No print
  // controls appear here — the executed copy has its own
  // print-safe shell (see `_render_contract_html`).
  const openPrintable = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const tok = localStorage.getItem('token') || '';
    // Preview the current active agreement in the professional
    // print-ready shell (server-side `_render_contract_html`).
    window.open(
      `${base}/api/icma/preview.html?v=${encodeURIComponent(agreement.agreement_version)}&token=${encodeURIComponent(tok)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="talent-agreement-page">
      <style>{`
        .icma-doc { color:#111827; line-height:1.7; font-size:15px; }
        .icma-doc > *:first-child { margin-top:0; }
        .icma-doc h1 { font-size:20px; font-weight:700; color:#0f172a;
          margin:28px 0 12px; padding-bottom:6px;
          border-bottom:1px solid #e2e8f0; line-height:1.3; }
        .icma-doc h2 { font-size:18px; font-weight:700; color:#0f172a;
          margin:24px 0 10px; line-height:1.3; }
        .icma-doc h3 { font-size:16px; font-weight:700; color:#0f172a;
          margin:20px 0 8px; line-height:1.3; }
        .icma-doc h4 { font-size:15px; font-weight:700; color:#0f172a;
          margin:16px 0 8px; }
        .icma-doc p { margin:0 0 12px; }
        .icma-doc ul, .icma-doc ol { margin:0 0 14px 22px; padding:0; }
        .icma-doc li { margin:4px 0; }
        .icma-doc strong { font-weight:700; color:#0f172a; }
        .icma-doc hr { border:0; border-top:1px solid #e2e8f0; margin:28px 0; }
        .icma-doc pre, .icma-doc code {
          font-family:inherit; background:transparent; padding:0;
          white-space:normal; display:inline;
        }
      `}</style>
      <div className="mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight" data-testid="agreement-title">
          {agreement.title}
        </h1>
        <div className="h-[2px] w-24 bg-[#03DFFC] rounded mt-2 mb-2" aria-hidden="true" />
      </div>
      <p className="text-xs sm:text-sm text-slate-500 mb-6 flex flex-wrap items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        <span>Version <span className="font-mono">{agreement.agreement_version}</span></span>
        <span aria-hidden="true">·</span>
        <span>Effective {agreement.effective_date}</span>
        <button
          onClick={openPrintable}
          className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-slate-300 hover:border-cyan-400 hover:text-cyan-700 print:hidden"
          data-testid="agreement-print-btn"
          type="button"
        >
          Print / Save PDF
        </button>
      </p>

      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent
          className="icma-doc p-6 sm:p-8"
          dangerouslySetInnerHTML={{ __html: agreement.body_html }}
          data-testid="agreement-body"
        />
      </Card>

      {identityError ? (
        <Card className="mt-6 border-2 border-amber-200 bg-amber-50/50" data-testid="agreement-identity-error">
          <CardContent className="p-5 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-slate-900">We couldn’t load your signer details</p>
                <p className="text-slate-700 mt-1">
                  You can read the full agreement above. To sign it, we need to
                  confirm your identity — this is usually a temporary hiccup.
                </p>
                <Button className="mt-3" onClick={retry} data-testid="agreement-identity-retry-btn">
                  Try again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Locked signer identity */}
      <Card className="mt-6 border-2 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4" /> Signer identity
            {idvVerified && (
              <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded ml-1">
                ID Verified
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {!idvVerified && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2" data-testid="idv-not-verified-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-amber-900 text-xs">
                Enter your full legal name exactly as it appears on your government-issued ID below. This is your signature on the agreement — do not use a nickname or preferred name. Completing ID verification will lock your verified legal name for all future documents.
              </div>
            </div>
          )}
          <dl className="grid grid-cols-3 gap-y-2 gap-x-2 text-sm">
            <dt className="text-slate-500 col-span-1">Profile name</dt>
            <dd className="col-span-2 text-slate-800" data-testid="signer-display-name">
              {identity?.display_name}
              <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                (display only)
              </span>
            </dd>
            <dt className="text-slate-500 col-span-1">Legal name</dt>
            <dd className="col-span-2 font-semibold" data-testid="signer-legal-name">
              {nameLocked ? (
                <>
                  {identity?.legal_name}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                    (from verified ID)
                  </span>
                </>
              ) : (
                <span className="text-slate-500 font-normal">You'll enter this below (as on your ID)</span>
              )}
            </dd>
            <dt className="text-slate-500">Account email</dt>
            <dd className="col-span-2 text-slate-800" data-testid="signer-email">{identity?.signer_email}</dd>
            <dt className="text-slate-500">User ID</dt>
            <dd className="col-span-2 font-mono text-xs text-slate-800 break-all" data-testid="signer-user-id">{identity?.user_id}</dd>
          </dl>
        </CardContent>
      </Card>

      {/* OTP */}
      <Card className="mt-4 border-2 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Step 1 — Verify it's you</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex items-center gap-2">
              <input
                type="radio" name="otpChannel" checked={otpChannel === 'email'}
                onChange={() => setOtpChannel('email')}
                data-testid="otp-channel-email"
              />
              <Mail className="w-4 h-4" /> Email <span className="text-slate-500 text-xs">{identity?.signer_email_masked}</span>
            </label>
            {identity?.has_phone && (
              <label className="flex items-center gap-2 sm:ml-4">
                <input
                  type="radio" name="otpChannel" checked={otpChannel === 'sms'}
                  onChange={() => setOtpChannel('sms')}
                  data-testid="otp-channel-sms"
                />
                <Phone className="w-4 h-4" /> Text <span className="text-slate-500 text-xs">{identity?.signer_phone_masked}</span>
              </label>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <Button
              type="button" variant="outline" onClick={requestOtp} disabled={sendingOtp}
              data-testid="otp-send-btn"
            >
              {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {challenge ? 'Resend code' : 'Send verification code'}
            </Button>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code"
              className="border border-slate-300 rounded px-3 py-2 w-40 font-mono text-lg tracking-widest text-center"
              data-testid="otp-code-input"
              disabled={!challenge}
            />
            {challenge && (
              <div className="text-xs text-slate-500" data-testid="otp-expiry">
                Expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Type-to-confirm + intent */}
      <Card className="mt-4 border-2 border-emerald-200 bg-emerald-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Step 2 — Sign</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-600 mb-1">
              {nameLocked
                ? 'Type your legal name exactly as shown above'
                : 'Type your full legal name exactly as it appears on your government-issued ID'}
            </label>
            <input
              type="text" value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              className={`w-full border rounded px-3 py-2 font-serif text-lg italic ${
                typedName && !nameMatches ? 'border-rose-300 bg-rose-50' :
                nameMatches ? 'border-emerald-400 bg-white' : 'border-slate-300'
              }`}
              placeholder={nameLocked ? (identity?.legal_name || '') : 'First Middle Last'}
              autoComplete="off" spellCheck={false}
              data-testid="typed-legal-name-input"
            />
            {typedName && !nameMatches && (
              <p className="text-xs text-rose-700 mt-1" data-testid="name-mismatch-msg">
                {nameLocked
                  ? 'This must exactly match your verified legal name. If your verified name is wrong, contact support to correct it.'
                  : 'Enter your full legal name (first and last) as it appears on your ID.'}
              </p>
            )}
            {!nameLocked && (
              <p className="text-[11px] text-slate-500 mt-1" data-testid="legal-name-help">
                Do not use a nickname or preferred name. This becomes your legal signature and will be used on your contracts, tax forms, and payment records.
              </p>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox" checked={confirmedIntent}
              onChange={(e) => setIntent(e.target.checked)}
              className="mt-1"
              data-testid="agreement-intent-checkbox"
            />
            <span>{ACCEPT_SCREEN_TEXT}</span>
          </label>

          <Button
            onClick={onSign} disabled={!canSign}
            className="bg-emerald-700 hover:bg-emerald-800 text-white w-full text-base py-6"
            data-testid="agreement-accept-btn"
          >
            {signing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            I Accept &amp; Electronically Sign
          </Button>
          {!canSign && !signing && (
            <p className="text-xs text-slate-500 text-center" data-testid="disabled-hint">
              Complete verification code, type your legal name, and confirm intent to enable signing.
            </p>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
